import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { 
  LoginRequest, 
  LoginResponse,
  LogoutResponse,
  ForgotPasswordResponse,
  VerifyOtpResponse,
  ResetPasswordResponse,
  ProfileConfirmResponse,
  ChangePasswordResponse,
  User, 
  AuthState 
} from '../models/auth.models';

// ============================================================================
// AUTH SERVICE - GERENCIAMENTO DE AUTENTICAÇÃO
// ============================================================================

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // ==========================================================================
  // PROPRIEDADES
  // ==========================================================================
  
  private readonly apiUrl = environment.apiUrl;
  
  private authStateSubject = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    permissions: [],
    processActionsAccessIds: [],
    requiresPasswordChange: false
  });
  
  // ==========================================================================
  // OBSERVABLES PÚBLICOS
  // ==========================================================================
  
  public authState$ = this.authStateSubject.asObservable();

  // ==========================================================================
  // CONSTRUCTOR
  // ==========================================================================

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.initializeAuthState();
  }

  // ==========================================================================
  // MÉTODOS DE AUTENTICAÇÃO
  // ==========================================================================

  /**
   * Realiza login do usuário
   * @param credentials Credenciais de login (email e password)
   * @returns Observable com a resposta do login
   */
  public login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, credentials)
      .pipe(
        tap((response) => {
          if (response.success) {
            this.setAuthData(response.data);
          }
        })
        // Sem catchError - deixa o erro passar direto para o componente tratar
      );
  }

  /**
   * Realiza logout do usuário
   * @returns Observable com a resposta do logout
   */
  public logout(): Observable<LogoutResponse> {
    const token = this.getToken();
    
    if (!token) {
      this.clearAuthData();
      return throwError(() => new Error('Token não encontrado'));
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    return this.http.post<LogoutResponse>(`${this.apiUrl}/auth/logout`, {}, { headers })
      .pipe(
        tap((response) => {
          if (response.success) {
            this.clearAuthData();
          }
        })
        // Sem catchError - deixa o erro passar direto para o componente tratar
      );
  }

  // ==========================================================================
  // MÉTODOS DE RECUPERAÇÃO DE SENHA
  // ==========================================================================

  /**
   * Envia email de recuperação de senha
   * @param email Email do usuário
   * @returns Observable com a resposta da API
   */
  public forgotPassword(email: string): Observable<ForgotPasswordResponse> {
    return this.http.post<ForgotPasswordResponse>(`${this.apiUrl}/auth/forgot-password`, { email });
    // Sem catchError - deixa o erro passar direto para o componente tratar
  }

  /**
   * Verifica o código OTP de recuperação de senha
   * @param email Email do usuário
   * @param otp Código OTP de 6 dígitos
   * @returns Observable com a resposta da API
   */
  public verifyOtp(email: string, otp: string): Observable<VerifyOtpResponse> {
    return this.http.post<VerifyOtpResponse>(`${this.apiUrl}/auth/verify-otp`, { email, otp });
    // Sem catchError - deixa o erro passar direto para o componente tratar
  }

  /**
   * Redefine a senha do usuário
   * @param email Email do usuário
   * @param otp Código OTP verificado
   * @param password Nova senha
   * @param passwordConfirmation Confirmação da nova senha
   * @returns Observable com a resposta da API
   */
  public resetPassword(
    email: string,
    otp: string,
    password: string,
    passwordConfirmation: string
  ): Observable<ResetPasswordResponse> {
    const payload = {
      email,
      otp,
      password,
      password_confirmation: passwordConfirmation
    };
    
    return this.http.post<ResetPasswordResponse>(`${this.apiUrl}/auth/reset-password`, payload);
    // Sem catchError - deixa o erro passar direto para o componente tratar
  }

  /**
   * Altera a senha do usuário autenticado (obrigatório no primeiro acesso)
   */
  public changePassword(
    currentPassword: string,
    password: string,
    passwordConfirmation: string
  ): Observable<ChangePasswordResponse> {
    const token = this.getToken();
    if (!token) {
      return throwError(() => new Error('Token não encontrado'));
    }

    return this.http.post<ChangePasswordResponse>(
      `${this.apiUrl}/auth/change-password`,
      {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation
      },
      { headers: { Authorization: `Bearer ${token}` } }
    ).pipe(
      tap((response) => {
        if (response.success) {
          this.setRequiresPasswordChange(false);
        }
      })
    );
  }

  // ==========================================================================
  // MÉTODOS AUXILIARES
  // ==========================================================================

  /**
   * Verifica se o usuário está autenticado
   * @returns True se autenticado
   */
  public isAuthenticated(): boolean {
    return this.authStateSubject.value.isAuthenticated;
  }

  /**
   * Obtém o usuário atual
   * @returns Usuário atual ou null
   */
  public getCurrentUser(): User | null {
    return this.authStateSubject.value.user;
  }

  /**
   * Obtém o token de autenticação
   * @returns Token ou null
   */
  public getToken(): string | null {
    return this.authStateSubject.value.token;
  }

  /**
   * Verifica se o usuário tem uma permissão específica
   * @param permission Permissão a verificar
   * @returns True se tem a permissão
   */
  public hasPermission(permission: string): boolean {
    return this.authStateSubject.value.permissions.includes(permission);
  }

  /**
   * Verifica se o usuário é admin
   * @returns True se é admin
   */
  public isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role?.slug === 'admin';
  }

  public isSuperAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role?.slug === 'super_admin';
  }

  /**
   * Verifica se o usuário é informador
   * @returns True se é informador
   */
  public isInformador(): boolean {
    const user = this.getCurrentUser();
    return user?.role?.slug === 'informador';
  }

  /**
   * Verifica se o usuário é colaborador
   * @returns True se é colaborador
   */
  public isColaborador(): boolean {
    const user = this.getCurrentUser();
    return user?.role?.slug === 'colaborador';
  }

  /**
   * Indica se o usuário pode visualizar notificações administrativas
   */
  public canViewNotifications(): boolean {
    return !this.isColaborador();
  }

  /**
   * Verifica se o colaborador pode executar ações em informadores do processo
   */
  public canAccessInformerActions(processId: string | null | undefined): boolean {
    if (!this.isColaborador()) {
      return true;
    }

    if (!processId) {
      return false;
    }

    return this.authStateSubject.value.processActionsAccessIds.includes(processId);
  }

  /**
   * IDs dos processos em que o colaborador tem permissão de ações/acompanhamento
   */
  public getProcessActionsAccessIds(): string[] {
    return [...this.authStateSubject.value.processActionsAccessIds];
  }

  /**
   * Obtém o email do usuário atual
   * @returns Email do usuário ou string vazia
   */
  public getUserEmail(): string {
    const user = this.getCurrentUser();
    return user?.email || '';
  }

  /**
   * Logout local sem chamada à API
   */
  public logoutLocal(): void {
    this.clearAuthData();
  }

  /**
   * Verifica se o usuário é informador (alias)
   * @returns True se é informador
   */
  public isAnyInformador(): boolean {
    return this.isInformador();
  }

  /**
   * Verifica se o informador precisa confirmar o cadastro (primeiro acesso)
   * @returns True se requires_profile_confirmation for true
   */
  public requiresProfileConfirmation(): boolean {
    const user = this.getCurrentUser();
    return !!(user?.informer?.requires_profile_confirmation);
  }

  /**
   * Verifica se o usuário deve trocar a senha no primeiro acesso
   */
  public requiresPasswordChange(): boolean {
    return this.authStateSubject.value.requiresPasswordChange;
  }

  /**
   * Redireciona após login ou troca de senha conforme role e flags do usuário
   */
  public navigateAfterAuth(): void {
    if (this.requiresPasswordChange()) {
      this.router.navigate(['/auth/change-password']);
      return;
    }

    const user = this.getCurrentUser();

    if (user?.role?.slug === 'super_admin') {
      this.router.navigate(['/painel']);
      return;
    }

    if (user?.role?.slug === 'admin' || user?.role?.slug === 'colaborador') {
      this.router.navigate(['/admin/dashboard']);
      return;
    }

    if (user?.role?.slug === 'informador') {
      if (this.requiresProfileConfirmation()) {
        this.router.navigate(['/auth/profile-confirm']);
      } else {
        this.router.navigate(['/protocol/cover']);
      }
      return;
    }

    this.router.navigate(['/protocol/cover']);
  }

  /**
   * Obtém os dados atualizados do usuário autenticado
   * @returns Observable com a resposta do endpoint /auth/me
   */
  public getMe(): Observable<{ success: boolean; data: { user: User; permissions?: string[]; process_actions_access_ids?: string[]; requires_password_change?: boolean } }> {
    const token = this.getToken();
    if (!token) {
      return throwError(() => new Error('Token não encontrado'));
    }
    return this.http.get<{ success: boolean; data: { user: User; permissions?: string[]; process_actions_access_ids?: string[]; requires_password_change?: boolean } }>(
      `${this.apiUrl}/auth/me`,
      { headers: { Authorization: `Bearer ${token}` } }
    ).pipe(
      tap((response) => {
        if (response.success) {
          const flag = response.data?.requires_password_change
            ?? response.data?.user?.requires_password_change;
          if (typeof flag === 'boolean') {
            this.setRequiresPasswordChange(flag);
          }
          if (response.data?.process_actions_access_ids !== undefined) {
            this.setProcessActionsAccessIds(response.data.process_actions_access_ids);
          }
          if (response.data?.user) {
            this.updateUserInState(response.data.user);
          }
        }
      })
    );
  }

  /**
   * Confirma o cadastro do informador (sem editar)
   * @returns Observable com a resposta da API
   */
  public confirmProfile(): Observable<ProfileConfirmResponse> {
    const token = this.getToken();
    if (!token) {
      return throwError(() => new Error('Token não encontrado'));
    }
    return this.http.post<ProfileConfirmResponse>(
      `${this.apiUrl}/auth/profile/confirm`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    ).pipe(
      tap((response) => {
        if (response.success && response.data?.user) {
          this.updateUserInState(response.data.user);
        }
      })
    );
  }

  /**
   * Atualiza o usuário no estado de autenticação e localStorage
   * @param user Novo objeto de usuário
   */
  public updateUserInState(user: User): void {
    const currentState = this.authStateSubject.value;
    if (!currentState.token) return;

    const authState: AuthState = {
      ...currentState,
      user
    };

    localStorage.setItem('userData', JSON.stringify(user));
    this.authStateSubject.next(authState);
  }

  /**
   * Limpa o email do usuário (faz logout)
   */
  public clearUserEmail(): void {
    this.logout();
  }

  /**
   * Define o email do usuário (método deprecated)
   * @deprecated Use o sistema de autenticação da API
   * @param email Email do usuário
   */
  public setUserEmail(email: string): void {
    console.warn('setUserEmail está deprecated. Use o sistema de autenticação da API.');
  }

  // ==========================================================================
  // MÉTODOS PRIVADOS
  // ==========================================================================

  /**
   * Inicializa o estado de autenticação do localStorage
   */
  private initializeAuthState(): void {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    const permissions = localStorage.getItem('permissions');
    const processActionsAccessIds = localStorage.getItem('processActionsAccessIds');
    const requiresPasswordChange = localStorage.getItem('requiresPasswordChange') === 'true';

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        this.authStateSubject.next({
          isAuthenticated: true,
          user,
          token,
          permissions: permissions ? JSON.parse(permissions) : [],
          processActionsAccessIds: processActionsAccessIds ? JSON.parse(processActionsAccessIds) : [],
          requiresPasswordChange
        });
      } catch (error) {
        this.clearAuthData();
      }
    }
  }

  /**
   * Define os dados de autenticação
   * @param data Dados de autenticação
   */
  private setAuthData(data: LoginResponse['data']): void {
    const requiresPasswordChange = !!data.requires_password_change;

    const authState: AuthState = {
      isAuthenticated: true,
      user: data.user,
      token: data.token,
      permissions: data.permissions,
      processActionsAccessIds: data.process_actions_access_ids ?? [],
      requiresPasswordChange
    };

    localStorage.setItem('authToken', data.token);
    localStorage.setItem('userData', JSON.stringify(data.user));
    localStorage.setItem('permissions', JSON.stringify(data.permissions));
    localStorage.setItem('processActionsAccessIds', JSON.stringify(data.process_actions_access_ids ?? []));
    localStorage.setItem('requiresPasswordChange', String(requiresPasswordChange));

    this.authStateSubject.next(authState);
  }

  private setRequiresPasswordChange(value: boolean): void {
    const currentState = this.authStateSubject.value;
    if (!currentState.isAuthenticated) {
      return;
    }

    localStorage.setItem('requiresPasswordChange', String(value));
    this.authStateSubject.next({
      ...currentState,
      requiresPasswordChange: value
    });
  }

  private setProcessActionsAccessIds(ids: string[]): void {
    const currentState = this.authStateSubject.value;
    if (!currentState.isAuthenticated) {
      return;
    }

    localStorage.setItem('processActionsAccessIds', JSON.stringify(ids));
    this.authStateSubject.next({
      ...currentState,
      processActionsAccessIds: ids
    });
  }

  /**
   * Limpa os dados de autenticação
   */
  private clearAuthData(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('permissions');
    localStorage.removeItem('processActionsAccessIds');
    localStorage.removeItem('requiresPasswordChange');

    this.authStateSubject.next({
      isAuthenticated: false,
      user: null,
      token: null,
      permissions: [],
      processActionsAccessIds: [],
      requiresPasswordChange: false
    });
  }

}
